import { useState, useEffect } from "react";
import { useApp } from "@/contexts/AppContext";
import api, { formatCurrency, formatDate } from "@/lib/api";
import { AlertTriangle, CheckCircle, Calculator, Plus, ArrowRight } from "lucide-react";

export default function ImpairmentPage() {
  const { activeEntity, entities } = useApp();
  const [tests, setTests] = useState([]);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showWizard, setShowWizard] = useState(false);
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    asset_id: "", indicator: "", fair_value_less_costs: 0,
    dcf_cashflows: [0, 0, 0, 0, 0], discount_rate: 12, terminal_value: 0,
    test_date: new Date().toISOString().substring(0, 10), notes: ""
  });
  const [result, setResult] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: t }, { data: a }] = await Promise.all([
        api.get("/impairments"),
        api.get("/assets", { params: { page_size: 1000, ...(activeEntity ? { entity_id: activeEntity.id } : {}) } })
      ]);
      setTests(t);
      setAssets(a.assets || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [activeEntity]);

  const runTest = async () => {
    try {
      const payload = {
        ...form,
        dcf_cashflows: form.dcf_cashflows.map(Number).filter(n => n !== 0)
      };
      const { data } = await api.post("/impairments", payload);
      setResult(data);
      setStep(3);
    } catch (e) { alert(e.response?.data?.detail || "Error"); }
  };

  const postImpairment = async (testId) => {
    if (!window.confirm("Post this impairment? This will reduce the asset's carrying amount and post a journal.")) return;
    try {
      const { data } = await api.post(`/impairments/${testId}/post`);
      alert(`Impairment posted!\nNew carrying: ${formatCurrency(data.new_carrying)}\nNew monthly dep: ${formatCurrency(data.new_monthly_dep)}`);
      setShowWizard(false);
      setResult(null);
      setStep(1);
      load();
    } catch (e) { alert(e.response?.data?.detail || "Error"); }
  };

  const selectedAsset = assets.find(a => a.id === form.asset_id);

  return (
    <div className="space-y-6" data-testid="impairment-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-semibold text-[#0B1020]">
            <AlertTriangle size={24} className="inline mr-2 text-[#C8A961]" />
            IAS 36 Impairment Testing
          </h1>
          <p className="text-sm text-[#6B7280] mt-1">Test assets for impairment using Fair Value Less Costs of Disposal or Value in Use (DCF)</p>
        </div>
        <button onClick={() => { setShowWizard(true); setStep(1); setResult(null); }} className="stratum-btn-primary flex items-center gap-2" data-testid="new-impairment-btn">
          <Plus size={16} /> New Impairment Test
        </button>
      </div>

      {/* Wizard */}
      {showWizard && (
        <div className="stratum-card p-6">
          {/* Step indicators */}
          <div className="flex items-center gap-4 mb-6">
            {[{n:1, l:"Select Asset"}, {n:2, l:"Enter Values"}, {n:3, l:"Result"}].map(s => (
              <div key={s.n} className={`flex items-center gap-2 ${step >= s.n ? "text-[#0B1020]" : "text-[#6B7280]"}`}>
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${step >= s.n ? "bg-[#C8A961] text-white" : "bg-[#E8E2D5] text-[#6B7280]"}`}>{s.n}</div>
                <span className="text-sm font-medium">{s.l}</span>
                {s.n < 3 && <ArrowRight size={14} className="text-[#E8E2D5] ml-2" />}
              </div>
            ))}
          </div>

          {/* Step 1: Select Asset */}
          {step === 1 && (
            <div className="space-y-4">
              <h3 className="font-display text-lg font-semibold">Step 1: Select Asset & Indicator</h3>
              <div>
                <label className="block text-xs font-medium text-[#6B7280] mb-1">Asset *</label>
                <select className="stratum-input w-full" value={form.asset_id} onChange={e => setForm({...form, asset_id: e.target.value})} data-testid="impairment-asset-select">
                  <option value="">Choose an asset...</option>
                  {assets.filter(a => a.status === "ACTIVE").map(a => (
                    <option key={a.id} value={a.id}>{a.tag} - {a.description} (Carrying: {formatCurrency(a.carrying_amount)})</option>
                  ))}
                </select>
              </div>
              {selectedAsset && (
                <div className="p-3 rounded-lg bg-[#FAF8F4] grid grid-cols-3 gap-3 text-sm">
                  <div><span className="text-[#6B7280] text-xs">Cost</span><div className="font-mono tabular-nums">{formatCurrency(selectedAsset.cost)}</div></div>
                  <div><span className="text-[#6B7280] text-xs">Accum Dep</span><div className="font-mono tabular-nums text-[#991B1B]">({formatCurrency(selectedAsset.accumulated_depreciation)})</div></div>
                  <div><span className="text-[#6B7280] text-xs">Carrying</span><div className="font-mono tabular-nums font-semibold">{formatCurrency(selectedAsset.carrying_amount)}</div></div>
                </div>
              )}
              <div>
                <label className="block text-xs font-medium text-[#6B7280] mb-1">Impairment Indicator (IAS 36.12)</label>
                <select className="stratum-input w-full" value={form.indicator} onChange={e => setForm({...form, indicator: e.target.value})}>
                  <option value="">Select indicator...</option>
                  <option value="MARKET_DECLINE">Significant decline in market value</option>
                  <option value="ADVERSE_CHANGES">Adverse changes in technology, market, economy, or law</option>
                  <option value="OBSOLESCENCE">Technological obsolescence</option>
                  <option value="PHYSICAL_DAMAGE">Physical damage</option>
                  <option value="RESTRUCTURING">Restructuring or discontinuation plans</option>
                  <option value="UNDERPERFORMANCE">Asset underperforming expectations</option>
                  <option value="REGULAR_REVIEW">Regular impairment review (annual)</option>
                </select>
              </div>
              <button onClick={() => setStep(2)} disabled={!form.asset_id} className="stratum-btn-primary" data-testid="impairment-next-btn">Next: Enter Values</button>
            </div>
          )}

          {/* Step 2: Enter Values */}
          {step === 2 && (
            <div className="space-y-4">
              <h3 className="font-display text-lg font-semibold">Step 2: Recoverable Amount</h3>
              <p className="text-sm text-[#6B7280]">Recoverable amount = higher of Fair Value Less Costs of Disposal (FVLCOD) and Value in Use (VIU). Enter one or both.</p>
              
              <div className="stratum-card p-5 border-l-4 border-l-[#C8A961]">
                <h4 className="font-medium text-[#0B1020] mb-3">Fair Value Less Costs of Disposal</h4>
                <div>
                  <label className="block text-xs font-medium text-[#6B7280] mb-1">FVLCOD (R)</label>
                  <input type="number" className="stratum-input w-full font-mono max-w-sm" value={form.fair_value_less_costs} onChange={e => setForm({...form, fair_value_less_costs: parseFloat(e.target.value) || 0})} data-testid="impairment-fvlcod-input" />
                  <p className="text-xs text-[#6B7280] mt-1">Market price minus costs to sell (broker fees, dismantling, transport)</p>
                </div>
              </div>

              <div className="stratum-card p-5 border-l-4 border-l-[#0B1020]">
                <h4 className="font-medium text-[#0B1020] mb-3">Value in Use (DCF)</h4>
                <div className="grid grid-cols-2 gap-4 mb-3">
                  <div><label className="block text-xs font-medium text-[#6B7280] mb-1">Discount Rate (%)</label><input type="number" step="0.1" className="stratum-input w-full font-mono" value={form.discount_rate} onChange={e => setForm({...form, discount_rate: parseFloat(e.target.value) || 0})} data-testid="impairment-rate-input" /></div>
                  <div><label className="block text-xs font-medium text-[#6B7280] mb-1">Terminal Value (R)</label><input type="number" className="stratum-input w-full font-mono" value={form.terminal_value} onChange={e => setForm({...form, terminal_value: parseFloat(e.target.value) || 0})} /></div>
                </div>
                <label className="block text-xs font-medium text-[#6B7280] mb-1">Projected Annual Cash Flows (R)</label>
                <div className="grid grid-cols-5 gap-2">
                  {form.dcf_cashflows.map((cf, i) => (
                    <div key={i}>
                      <label className="text-[10px] text-[#6B7280]">Year {i + 1}</label>
                      <input type="number" className="stratum-input w-full font-mono text-sm" value={cf} onChange={e => {
                        const arr = [...form.dcf_cashflows];
                        arr[i] = parseFloat(e.target.value) || 0;
                        setForm({...form, dcf_cashflows: arr});
                      }} data-testid={`dcf-year-${i+1}`} />
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex gap-3">
                <button onClick={() => setStep(1)} className="px-4 py-2 rounded-lg border border-[#E8E2D5] text-sm">Back</button>
                <button onClick={runTest} className="stratum-btn-primary flex items-center gap-2" data-testid="run-impairment-btn">
                  <Calculator size={14} /> Calculate Impairment
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Result */}
          {step === 3 && result && (
            <div className="space-y-4">
              <h3 className="font-display text-lg font-semibold">Step 3: Result</h3>
              <div className={`stratum-card p-6 ${result.is_impaired ? "border-l-4 border-l-[#991B1B] bg-[#991B1B]/5" : "border-l-4 border-l-[#065F46] bg-[#065F46]/5"}`}>
                <div className="flex items-center gap-3 mb-4">
                  {result.is_impaired ? <AlertTriangle size={24} className="text-[#991B1B]" /> : <CheckCircle size={24} className="text-[#065F46]" />}
                  <h4 className={`font-display text-xl font-semibold ${result.is_impaired ? "text-[#991B1B]" : "text-[#065F46]"}`}>
                    {result.is_impaired ? `IMPAIRED — Loss: ${formatCurrency(result.impairment_loss)}` : "NOT IMPAIRED"}
                  </h4>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div><span className="text-[#6B7280] text-xs">Carrying Amount</span><div className="font-mono tabular-nums font-semibold">{formatCurrency(result.carrying_amount)}</div></div>
                  <div><span className="text-[#6B7280] text-xs">FVLCOD</span><div className="font-mono tabular-nums">{formatCurrency(result.fair_value_less_costs)}</div></div>
                  <div><span className="text-[#6B7280] text-xs">Value in Use</span><div className="font-mono tabular-nums">{formatCurrency(result.value_in_use)}</div></div>
                  <div><span className="text-[#6B7280] text-xs">Recoverable ({result.recoverable_basis})</span><div className="font-mono tabular-nums font-semibold">{formatCurrency(result.recoverable_amount)}</div></div>
                </div>
              </div>
              <div className="flex gap-3">
                <button onClick={() => { setShowWizard(false); setResult(null); }} className="px-4 py-2 rounded-lg border border-[#E8E2D5] text-sm">Close</button>
                {result.is_impaired && (
                  <button onClick={() => postImpairment(result.id)} className="px-4 py-2 rounded-lg bg-[#991B1B] text-white text-sm font-medium hover:bg-[#7F1D1D] flex items-center gap-2" data-testid="post-impairment-btn">
                    Post Impairment Journal ({formatCurrency(result.impairment_loss)})
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Test History */}
      {!showWizard && (
        <div className="stratum-card overflow-hidden">
          <div className="p-4 border-b border-[#E8E2D5]"><h3 className="font-display text-base font-semibold">Impairment Test History</h3></div>
          <table className="w-full stratum-table" data-testid="impairment-table">
            <thead><tr><th>Asset</th><th>Date</th><th className="text-right">Carrying</th><th className="text-right">Recoverable</th><th className="text-right">Loss</th><th>Basis</th><th>Status</th></tr></thead>
            <tbody>
              {loading ? <tr><td colSpan={7} className="text-center py-8 text-[#6B7280]">Loading...</td></tr> :
               tests.length === 0 ? <tr><td colSpan={7} className="text-center py-8 text-[#6B7280]">No impairment tests yet</td></tr> :
               tests.map(t => (
                <tr key={t.id}>
                  <td><span className="font-mono text-sm">{t.asset_tag}</span> <span className="text-xs text-[#6B7280]">{t.asset_description}</span></td>
                  <td className="font-mono text-xs tabular-nums">{formatDate(t.test_date)}</td>
                  <td className="text-right font-mono tabular-nums text-sm">{formatCurrency(t.carrying_amount)}</td>
                  <td className="text-right font-mono tabular-nums text-sm">{formatCurrency(t.recoverable_amount)}</td>
                  <td className={`text-right font-mono tabular-nums text-sm font-medium ${t.is_impaired ? "text-[#991B1B]" : "text-[#065F46]"}`}>{t.is_impaired ? formatCurrency(t.impairment_loss) : "—"}</td>
                  <td className="text-xs">{t.recoverable_basis}</td>
                  <td><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${t.status === "POSTED" ? "bg-[#065F46]/10 text-[#065F46]" : "bg-[#C8A961]/10 text-[#C8A961]"}`}>{t.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
